/**
 * The route test's raw rows, one per gateway + sender tested (19/10/2026):
 * drops, SMS, delivered %, bot %, clicks and CR. Since 21/10/2026 the
 * ranking leads the section and these sit behind "Per sender".
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { ChevronDown, Trophy } from 'lucide-react';
import type { DropGenRouteTestSample } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

const GRID = 'grid grid-cols-[minmax(170px,1.2fr)_minmax(130px,1fr)_70px_90px_84px_72px_72px_72px] items-center gap-3 px-5';
const pct = (v: number | null) => (v === null ? '—' : `${(v * 100).toFixed(v * 100 >= 10 ? 0 : 1)}%`);
const n = (v: number) => v.toLocaleString('en-US');

const SampleRow: FC<{ s: DropGenRouteTestSample; winner: boolean }> = ({ s, winner }) => (
  <div className={`${GRID} border-t border-border/30 py-1.5 text-xs ${winner ? 'bg-success/5' : ''}`} data-testid="route-test-sample">
    <span className="flex min-w-0 items-center gap-1.5">
      {winner ? <Trophy className="h-3.5 w-3.5 shrink-0 text-success" aria-label="winner" /> : null}
      <span className="truncate font-medium" title={s.routeName ?? undefined}>{s.routeName ?? `Gateway ${s.routeId}`}</span>
    </span>
    <span className="truncate font-mono text-[11px]" title={s.sender}>{s.sender}</span>
    <span className="text-right font-mono tabular-nums">{s.dropsSent}</span>
    <span className="text-right font-mono tabular-nums">{n(s.smsSent)}</span>
    <span className="text-right font-mono tabular-nums">{pct(s.deliveredRate)}</span>
    <span className="text-right font-mono tabular-nums">{pct(s.botRate)}</span>
    <span className="text-right font-mono tabular-nums">{n(s.uniqueClicks)}</span>
    <span className="text-right font-mono tabular-nums">{pct(s.cr)}</span>
  </div>
);

export const PerSender: FC<{ samples: DropGenRouteTestSample[]; isWinner: (s: DropGenRouteTestSample) => boolean }> = ({ samples, isWinner }) => {
  const [open, setOpen] = useState(false);
  if (samples.length === 0) return null;
  return (
    <div className="mb-1">
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="mx-5 flex items-center gap-1 text-[11px] font-medium text-primary hover:underline">
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} aria-hidden />
        Per sender ({samples.length})
      </button>
      {open ? (
        <div className="overflow-x-auto">
          <div className="min-w-[860px]">
            <div className={`${GRID} py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground`}>
              <span>Gateway</span><span>Sender</span><span className="text-right">Drops</span><span className="text-right">SMS</span><span className="text-right">Delivered</span><span className="text-right">Bot</span><span className="text-right">Clicks</span><span className="text-right">CR</span>
            </div>
            {samples.map((s) => (
              <SampleRow key={`${s.routeId}|${s.sender}`} s={s} winner={isWinner(s)} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
